/**
 * Client-controlled sharing pillars (steward-app#739, docs/26 §Phase 5).
 *
 * A family connects an advisor firm and, today, that firm's membership carries
 * `steward-advisor` — which holds `inst:read`, described by appcore's own comment as
 * *"workspace-wide with no type axis"*. Absent folder ACLs the firm can therefore read
 * the whole vault. This table is the axis that fixes that: one capability string per
 * **disclosure domain**, a small role holding only that string, composed onto the
 * membership (`roleIds` is an array; permissions are the union), so scope is per
 * connection and two firms connected to one family get independent scopes for free.
 *
 * ⚠️ **That sentence read "ONE small role" until steward-app#1260 (2026-09-15), and an area
 * may now compose more than one.** `people` composes two: the read role and a
 * tax-identification reveal role ({@link SharingPillar.additionalRoleIds} carries the
 * argument). The rest of this docblock still holds exactly as written — what remains one per
 * area is the {@link SharingPillar.capability} that its {@link SharingPillar.typeCodes}
 * declare, and the reveal capability is declared on no type at all.
 *
 * ## The set is deliberately coarse, and deliberately the app's own navigation
 *
 * "Share my contacts" is a decision a person can make. "Share contact #47" is a filing
 * system. So the areas are the words the family already reads in the SPA's navigation
 * (`src/components/Layout.tsx` `NAV` and the pillar tab bars, docs/04-navigation-ia.md
 * §2/§5) — whether that word names a sidebar pillar or a pillar TAB — and nothing finer.
 *
 * ⚠️ **This sentence read "the SPA's flat navigation pillars … the same words the family
 * already reads in the sidebar" until steward-app#1425, and that went false when Contacts
 * folded into the People pillar as its second tab.** Both halves of the People pillar are
 * now tab labels: `contacts` is the Contacts tab, and `people` is labelled **Family** after
 * the Family tab (see {@link SharingPillar.label}). Nothing in code ever asserted the 1:1
 * with `NAV` — `pillars.test.ts` ties nothing to it and `WhatYouShare.tsx` maps over
 * {@link SHARING_PILLARS} with no such assumption — so this was prose asserting more than
 * its code did, which is the failure CLAUDE.md names. The ARGUMENT survives the correction
 * intact: the point was never the sidebar specifically, it was that a family should be
 * offered a choice in vocabulary they already navigate by.
 *
 * Dashboard is not a pillar here: it is a rendering of portfolio data (net worth, accounts,
 * attention items), not a separate domain, and giving it its own capability would let a
 * client make a choice with no honest meaning.
 *
 * ## ⚠️ THIS TABLE IS ARMED, EXCEPT FOR WHAT {@link PILLAR_GRANTED_NOT_YET_ARMED} NAMES
 *
 * A capability only bites when a CTYPE declares it as `accessControl.readCapability`.
 * Phase 2b (steward-app#739) armed the four pillars that existed then, and `people`
 * (steward-app#1260) was armed by its own phase 2 on 2026-09-14. The rule for every pillar
 * is: **every `typeCodes` entry below carries its pillar's string** in
 * `STEWARD_APP_TYPE_DEFS` — asserted in both directions by `pillars.test.ts`
 * (`EVERY PILLAR IS ARMED`), which replaced the `NOTHING IS ARMED` block that guarded the
 * gap between the two phases.
 *
 * **The one exception is a pillar listed in {@link PILLAR_GRANTED_NOT_YET_ARMED}**, which
 * is mid-sequence: granted here, not yet declared on its types. The two assertions that
 * REQUIRE a declaration skip it, and opposite-direction assertions cover it instead — NO
 * pack type may declare its capability, and its types must declare no read gate at all,
 * while it sits on that list. So arming early reds the suite. **That list is EMPTY as of
 * 2026-09-14**, which is the steady state, not a sign the mechanism is unused; a pillar
 * sits on it only for the minutes-to-days between the grant deploy and the arm deploy, for
 * the reason the ordering section below gives. Read the heading above as a standing rule,
 * not as a claim that something is unarmed today — the constant is the authority, and it
 * is pinned as an exact literal by `pillars.test.ts`.
 *
 * ⚠️ **An unarmed pillar is still a live entry in this table, so the DISCLOSURE surfaces
 * must not render it. That is {@link LIVE_SHARING_PILLARS}, and it is a narrow exception:
 * everything else — every grant, and in particular every advisor-role STRIP list — keeps
 * using the total table.** Read that constant's docblock before adding a consumer either
 * way; getting the split backwards is a cross-firm leak in one direction and a control
 * that lies in the other.
 *
 * **The grant came first, and that ordering is why it was safe.** Arming a
 * `readCapability` affects **every reader**, not just advisors: gating `contact` on
 * `contacts:read` withholds contacts from the family's own members until they hold it. So
 * the sequence was, and for any FUTURE pillar must be:
 *
 *   1. Grant to the family roles + seed the pack (this table, and `packRoles.ts`).
 *      Verify at source that the seeded roles carry the strings, per environment.
 *   2. Minutes later, in a SEPARATE artifact, arm the declarations.
 *
 * "Grant and arm in one seed run" is not sufficient even in that order.
 * `auth/permissions.ts` and the pack custom-type resolver each hold an independent
 * 60-second in-process cache, so a warm Lambda can serve the STALE role (no capability)
 * while its CTYPE cache expires and picks up the ARMED type — up to ~60s where every
 * family member is denied, per warm container, with the seed reporting complete
 * success. (Discovered 2026-08-24 during appcore#449's `writeCapability` work.)
 *
 * The grant shipped 2026-08-24. `appcore-stage` and `appcore-prod` were queried directly
 * on 2026-08-27, before arming: `steward-admin`, `steward-member` and `steward-viewer` each
 * hold all four strings; `steward-advisor` holds none. `appcore-dev` was verified the same
 * day by the founder and recorded on steward-app#739; it was NOT re-queried for the arming
 * PR, whose author could not refresh the dev SSO token. Query it before trusting the dev
 * deploy. (All of that is a dated reading taken by other people, kept attributed rather
 * than restated as current; it says nothing about any environment today.)
 *
 * **EVERY pillar added to this table after the first four repeats the whole
 * sequence** — a new string is granted nowhere until a re-seed has run in the target
 * environment, so adding one to `typeCodes` and to a type definition in the same PR is
 * the exact collapse the ordering above forbids. `people` (steward-app#1260/#1425) is the
 * fifth and it RAN that sequence: the grant shipped first, the arm followed a release
 * later, and {@link PILLAR_GRANTED_NOT_YET_ARMED} is how the tests expressed the in-between
 * state rather than letting the grant PR red or, worse, pushing its author to collapse the
 * two. That constant survives its empty list for the pillar after this one.
 *
 * The `people` grant was read back in `appcore-dev` on 2026-09-14, before the arm PR was
 * written: `CTYPE#person` carried `writeCapability` and NO `readCapability`, while
 * `CTYPE#contact` carried its armed `contacts:read` in the same query shape as the positive
 * control; the Admin, Member, Viewer and `steward-share-people` roles each carried
 * `people:read` and the Advisor role did not. Stage and prod were not re-queried for the arm
 * PR — the grant is claimed there only from the release tag it shipped in. (Reading taken by
 * the coordinator of this branch, not by this file's author; a dated point-in-time fact, and
 * it says nothing about any environment today.)
 *
 * Two consequences of being armed that are easy to forget:
 *
 * - **The gate binds writes too.** appcore's `requiredCapabilities` adds `readCapability`
 *   under BOTH intents ("you may not touch what you may not read"), so withholding an
 *   area withholds writing into it as well.
 * - **Disarming IS a redeploy, and nothing at appcore stops it.** The drop-ratchet
 *   (`assertReadCapabilityRatchet`) guards only `CustomTypeRepository.update`, the API
 *   plane; `scripts/seed-steward-app.ts` Puts pack CTYPE rows directly to DynamoDB, so a
 *   revert of the declarations plus a `seed-pack` run overwrites the armed rows with
 *   unarmed ones. Convenient as a rollback, dangerous as an accident — `pillars.test.ts`'s
 *   exact-literal assertion is the only guard against the accident.
 *
 * ## What is NOT a pillar, and why each one is a decision rather than an oversight
 *
 * - **Credentials.** Already withheld from advisors unconditionally, by the same
 *   mechanism: `credential`/`credential-vault` declare `readCapability:
 *   'credential:read'` and `steward-advisor` omits it (steward-app#639/#643). That is
 *   not a client choice and must not become one — the advisor role is *defined* by that
 *   omission, and turning it into a toggle would undo the feature. Credentials are the
 *   worked example this table copies, not a member of it.
 * - **`chat-session`.** The family's own conversations with the assistant. Not a pillar
 *   anyone would think to "share", and not the advisor's business — so it is withheld by
 *   a FIXED rule, the `credential:read` shape, and stays out of this table permanently.
 *   That rule is steward-app#932 and lives in `assistantAccess.ts`: `assistant:read` is
 *   granted to the three family roles and omitted from `steward-advisor`. Granted in
 *   phase 1 (#933) and **armed in phase 2** — `chat-session` now declares
 *   `accessControl.readCapability: 'assistant:read'`, so an advisor's `inst:read` no
 *   longer reaches it through appcore's generic entities route. Between two family MEMBERS
 *   the type also declares creator ownership (steward-app#590, `CREATOR_OWNED_TYPE_CODES`):
 *   appcore rel-2026.10.26 added the primitive and steward-app arms it from the release that
 *   carries #590, so verify each environment. The façade's per-owner `_cb` filter stays as
 *   defence in depth.
 * - **`feedback`.** Vestigial: feedback moved to appcore's own `/w/{ws}/feedback`
 *   capability, so nothing writes this type any more and a live note is not reachable
 *   through the plane a pillar would gate. Ungated on that basis, stated rather than
 *   assumed — `assistantAccess.ts` carries the same note plus the residual it leaves.
 * - **`advisor-connection`, `advisor-intake`, `onboarding-request`, `onboarding-confirmation`.** The channel's own
 *   machinery. Gating `advisor-connection` on a client-toggled capability would let a client
 *   switch off the record that describes the switch, and would break People → Advisors.
 *   `onboarding-request` is the firm's request to the family, and founder ruling 4 on
 *   steward-app#1426 (2026-09-16) keeps it read-ungated: every connected firm must be able to
 *   read its own request, and the façade filters by firm. `onboarding-confirmation` is the
 *   family's answer to that request and is ungated on the same ruling, filtered by its
 *   `firmTenantId` attribute.
 *
 * `PILLAR_UNGATED_TYPE_CODES` names all of those explicitly, so that
 * `pillars.test.ts`'s exhaustiveness check forces a deliberate answer for every FUTURE
 * type added to the pack rather than letting it default to "shared".
 */

/** The stable id of a sharing area. Used in copy keys, API area names, and nothing else. */
export type SharingPillarId = 'portfolio' | 'documents' | 'contacts' | 'entities' | 'people';

export interface SharingPillar {
  /** Stable id. This is what the client's request names — never a role id (invariant 1
   *  of `services/steward-app-api/src/handlers/advisor.ts`: the server maps area → role). */
  readonly id: SharingPillarId;
  /**
   * The words the family reads in navigation for this area, whether that is a sidebar
   * pillar or a tab within one. Copy says "What you share", never "scopes".
   *
   * It said "the SPA sidebar label, verbatim" until steward-app#1425, and that went false
   * the moment a pillar stopped being one sidebar item: `people` is labelled **Family**,
   * the name of the People pillar's Family TAB, because People → Advisors → What you share
   * (`src/pages/people/WhatYouShare.tsx`) renders these as one flat list of disclosure
   * areas and "People" there would sit beside `contacts`, its own sibling tab, as though
   * the two were peers. Founder's decision, 2026-09-14.
   *
   * So this is NOT a 1:1 mirror of `src/components/Layout.tsx`'s `NAV`, and nothing
   * asserts that it is. What it must stay is the vocabulary the family already recognises
   * from navigation — that is the argument in the module docblock for why the areas are
   * coarse, and it survives a pillar whose words come from a tab.
   */
  readonly label: string;
  /**
   * The capability string. `resource:verb`, lowercase, two segments, no wildcard —
   * appcore's `CAPABILITY_REQUIREMENT` (`packages/core/src/auth/capability-grammar.ts`)
   * refuses anything else on a type declaration.
   *
   * **Frozen once seeded.** Renaming one after a seed leaves the old string granted on
   * persisted role rows in every environment and the new string granted nowhere, which
   * is indistinguishable from an unseeded pack. Plural resource names here (they name a
   * collection the family recognises, and match #739 and docs/26 §Phase 5 verbatim);
   * `credential:read` is singular and predates this table — that inconsistency is
   * deliberate history, not something to "tidy" by renaming either side.
   */
  readonly capability: string;
  /**
   * The role that holds ONLY this capability, minted by `scripts/seed-steward-app.ts`.
   *
   * A **fresh ULID is mandatory**: appcore's role cache (`auth/permissions.ts`) is keyed
   * by `roleId` alone with no pack segment and a 60s TTL, so a duplicate id lets a warm
   * container serve one pack's role to another pack's workspace for up to a minute.
   */
  readonly roleId: string;
  /**
   * FURTHER roles this area composes, beyond {@link SharingPillar.roleId}. `[]` for four
   * of the five pillars, and that is the expected shape — a second entry is a deliberate
   * decision about one area, not a pattern to spread.
   *
   * ## Why a second role id rather than a second capability on the first role
   *
   * `people` carries one (steward-app#1260): `steward-share-people-tax-id`, holding only
   * `people:reveal-tax-id`, the capability appcore's SSN reveal route requires
   * (crossmarc/appcore#668). It is a SEPARATE role from `steward-share-people` on purpose.
   * Putting both strings on the single existing role would be the smaller diff and would
   * need a DATA MIGRATION to undo: every advisor membership already written would carry
   * one id granting both, so splitting "see the Family area" from "reveal a tax
   * identification number" into two family-facing switches would mean minting a role and
   * rewriting memberships. Two ids from the start makes that split a code change and no
   * migration, because a membership carrying both already reads as both switches on.
   *
   * ## What this shape deliberately does NOT do
   *
   * These ids are NOT part of the arm machinery. {@link SharingPillar.capability} is still
   * the one string this area's {@link SharingPillar.typeCodes} declare, and
   * `people:reveal-tax-id` is declared on no type at all — it is a ROUTE requirement, which
   * is why it can exist without a sixth pillar entry and without an entry in
   * {@link PILLAR_GRANTED_NOT_YET_ARMED}. `pillars.test.ts`'s `EVERY PILLAR IS ARMED` block
   * compares declared strings against `[pillar.capability]` alone, so a capability that
   * never reaches a type declaration never reaches that block either. Keep it that way: a
   * reveal capability declared as a type's `readCapability` would withhold the whole
   * `person` type from anyone lacking it, family VIEWERs included.
   *
   * ## A fresh ULID is mandatory, per id
   *
   * Same rule and same reason as {@link SharingPillar.roleId}: appcore's role cache is keyed
   * by `roleId` alone. In particular `01HZSTEWRDPERSN000000001` must not be repurposed or
   * edited — it is named by real memberships wherever the `people` grant has been seeded,
   * which the module docblock above dates and attributes per environment rather than claiming
   * as current. Repurposing a seeded id is the one mistake here with no clean recovery.
   *
   * ⚠️ {@link PILLAR_ROLE_IDS} spreads these, which is what puts them in both advisor-scope
   * STRIP lists (`roles.ts`). An id here that did not reach those lists is
   * steward-app#732/#787's cross-firm leak arriving by a new route, so `pillars.test.ts`
   * asserts the strip lists over EVERY role id of every pillar, not just the primary.
   */
  readonly additionalRoleIds: readonly string[];
  /**
   * Roles this area composes when the family has it OFF (steward-app#1950). `[]` for every
   * pillar but `documents`.
   *
   * `documents` carries {@link DOCUMENTS_SHARED_FOLDER_ROLE_ID}: `documents:read` plus
   * appcore's read confinement for that same string (crossmarc/appcore#786, appcore docs/06
   * §18.7). With Documents off the firm therefore reads ONLY folders whose ACL names them at
   * `read`: the "Shared with «firm»" folder (#732), and ALSO any folder the family shared
   * with one of the firm's advisors by name through "Who can access" (`folderAcl.ts` writes
   * a `USR` entry at `read` for any member, advisors included, and holds back only the
   * custody verb). What it does not read: an unrestricted folder, an unfiled document, a
   * folder whose ACL does not name them. The What you share note says both halves. Founder
   * ruling 2026-09-30: switching Documents off keeps the shared folder.
   *
   * ⚠️ **In {@link PILLAR_ROLE_IDS}, and it must stay there.** That is what puts these ids in
   * the advisor-scope STRIP lists (`roles.ts`), and here that is a security property, not
   * tidiness: an ACL entry naming ANY of the caller's roles names the caller (appcore docs/06
   * §18.7, "What it does not bind"), so this id harvested onto a family-only folder as a
   * FAMILY principal would open that folder to every confined firm. It is also what makes
   * `setSharedAreas` strip it when Documents is turned back on.
   *
   * ⚠️ **Never beside a FAMILY role, and never beside this pillar's own `roleId`.** appcore
   * resolves read confinement as a UNION over the membership's roles, so a person the family
   * ALSO made a Viewer or Member would be confined to the shared folder across the whole
   * vault, and a firm holding both the area role and this one would be confined while the
   * area reads as shared. `withoutConflictingConfinement` (`roles.ts`) drops it in both cases;
   * that function's own docblock names the façade writes that apply it.
   */
  readonly offRoleIds: readonly string[];
  /** Every pack type in this disclosure domain. Each one declares this pillar's
   *  `capability` as its `accessControl.readCapability` in `STEWARD_APP_TYPE_DEFS`
   *  (phase 2b) — `pillars.test.ts` pins the correspondence in both directions, so a
   *  code added here without arming the type reds, and so does the reverse.
   *
   *  ⚠️ **Unless the pillar is on {@link PILLAR_GRANTED_NOT_YET_ARMED}**, where the
   *  correspondence is asserted in the OPPOSITE direction for the length of the grant →
   *  arm gap: these codes must declare the capability NOWHERE. Same test file, different
   *  assertion; neither direction is unguarded. */
  readonly typeCodes: readonly string[];
}

/**
 * The capability appcore's tax-identification reveal route requires (crossmarc/appcore#668,
 * steward-app#1260).
 *
 * ⚠️ **A ROUTE requirement, not a type declaration.** No `STEWARD_APP_TYPE_DEFS` entry
 * declares this as an `accessControl.readCapability` and none may: arming it on `person`
 * would withhold the whole person record from every principal lacking it, including a
 * family's own VIEWER, which is not what a per-field reveal means. It therefore never enters
 * the grant → arm ordering the module docblock sets out, and it is NOT a member of
 * {@link PILLAR_CAPABILITIES}.
 *
 * **Frozen once seeded**, for the same reason every pillar capability is: renaming it leaves
 * the old string on persisted role rows in every environment and the new string granted
 * nowhere.
 *
 * Granted to `steward-admin` and `steward-member` only among the base roles, plus the
 * composable {@link PEOPLE_TAX_ID_REVEAL_ROLE_ID} role. **Deliberately NOT
 * `steward-viewer`**, mirroring `credential:reveal` (`packRoles.ts`): a read-only family
 * member sees that a tax identification number is on file and cannot decrypt it. Also NOT
 * `steward-advisor` — what a firm may reveal is composed per connection, exactly as every
 * pillar capability is.
 */
export const PEOPLE_REVEAL_TAX_ID_CAPABILITY = 'people:reveal-tax-id';

/**
 * The composable role holding {@link PEOPLE_REVEAL_TAX_ID_CAPABILITY} and nothing else,
 * seeded as `steward-share-people-tax-id` (`packRoles.ts`).
 *
 * A FRESH id, minted 2026-09-15 for steward-app#1260 and never seeded before this branch.
 * It follows the same 24-character `[0-9A-Z]` shape as every other declared pack role id
 * (`packRoles.test.ts` asserts length and alphabet against the ids already in the tree, not
 * against a 26-character ULID spec none of them satisfy).
 *
 * ⚠️ Referenced from the `people` pillar's {@link SharingPillar.additionalRoleIds} — read
 * that docblock for why the reveal is its own role rather than a second string on
 * `steward-share-people`.
 */
export const PEOPLE_TAX_ID_REVEAL_ROLE_ID = '01HZSTEWRDPTAXID00000001';

/**
 * The role composed onto a firm's membership when the family has DOCUMENTS off
 * (steward-app#1950): `documents:read`, read-confined to that same capability
 * (crossmarc/appcore#786), seeded as `steward-share-documents-shared-folder` (`packRoles.ts`).
 * Read the `documents` pillar's {@link SharingPillar.offRoleIds} before changing anything.
 *
 * A FRESH id, minted 2026-09-30 and never seeded before this branch.
 *
 * ⚠️ **Fails OPEN without appcore#786.** On an appcore that does not read
 * `readConfinedCapabilities` this role is a plain `documents:read` grant, so a firm with
 * Documents off would read every document. appcore must be live in an environment before
 * this reaches it. The row is also load-bearing for WITHDRAWAL in the same way
 * `areaRoleIds`' ⚠️⚠️ describes, and here a missing row fails open too (appcore docs/06
 * §18.7): never delete it while a membership may name it.
 */
export const DOCUMENTS_SHARED_FOLDER_ROLE_ID = '01HZSTEWRDDCSHF000000001';

/**
 * The five shareable areas.
 *
 * A pillar spanning several types declares the IDENTICAL capability on EVERY one of
 * them — the lesson of `credential`/`credential-vault` (steward-app#643):
 * declaring it on the child alone still lets a caller list the parent, and the parent is
 * often the row whose mere existence discloses the thing (a vault names the institution;
 * a `document-folder` names the folder).
 */
export const SHARING_PILLARS: readonly SharingPillar[] = [
  {
    id: 'portfolio',
    label: 'Portfolio',
    capability: 'portfolio:read',
    roleId: '01HZSTEWRDPORTF000000001',
    additionalRoleIds: [],
    offRoleIds: [],
    // Everything the money view is made of, including the Dashboard's net-worth history
    // and the connected-institution list. `account-connection` belongs here rather than
    // in a pillar of its own: it names the family's banks, which is portfolio disclosure
    // by any honest reading.
    typeCodes: [
      'account', 'account-connection', 'asset', 'asset-lot', 'asset-event', 'valuation',
      'market-price', 'liability', 'liability-balance', 'net-worth-snapshot', 'net-worth-baseline',
    ],
  },
  {
    id: 'documents',
    label: 'Documents',
    capability: 'documents:read',
    roleId: '01HZSTEWRDDOCMT000000001',
    additionalRoleIds: [],
    // Documents OFF composes the shared-folder-only role instead of nothing (#1950).
    offRoleIds: [DOCUMENTS_SHARED_FOLDER_ROLE_ID],
    // `document-folder` is included for the reason above AND because `document` is
    // access-parented to it: a folder the caller cannot read already hides its documents,
    // so gating only the child would be half a gate.
    //
    // Turning this area OFF keeps the "Shared with «firm»" folder (docs/26 §Phase 4a,
    // steward-app#731/#732; founder ruling 2026-09-30, steward-app#1950). Until #1950 it
    // took the folder away too, because withholding `documents:read` withholds the whole
    // type. `offRoleIds` below is how it no longer does.
    typeCodes: ['document', 'document-folder'],
  },
  {
    id: 'contacts',
    label: 'Contacts',
    capability: 'contacts:read',
    roleId: '01HZSTEWRDCNTCT000000001',
    additionalRoleIds: [],
    offRoleIds: [],
    typeCodes: ['contact'],
  },
  {
    id: 'entities',
    label: 'Entities',
    capability: 'entities:read',
    roleId: '01HZSTEWRDENTTY000000001',
    additionalRoleIds: [],
    offRoleIds: [],
    typeCodes: ['legal-entity'],
  },
  {
    id: 'people',
    // "Family", not "People" — founder's decision, 2026-09-14. See `SharingPillar.label`:
    // this string is read in People → Advisors → What you share, a FLAT list, where "People" would
    // stand next to `contacts`, the other tab of the same pillar (#1425).
    label: 'Family',
    capability: 'people:read',
    roleId: '01HZSTEWRDPERSN000000001',
    // ⚠️ The ONE pillar with a second role (steward-app#1260, 2026-09-15). It holds only
    // `people:reveal-tax-id` and is a separate role, not a second string on the role above,
    // so that splitting "see the Family area" from "reveal a tax identification number"
    // into two switches later is a code change with NO data migration. Read
    // `SharingPillar.additionalRoleIds` for the whole argument, including why this
    // capability is declared on no type and so never enters the arm machinery.
    //
    // ⚠️ COMPOSED onto real memberships since steward-app#1260 PR 4 (2026-09-15).
    // `areaRoleIds` in the façade is a `flatMap` over `[roleId, ...additionalRoleIds]`, so
    // choosing the Family area writes BOTH ids onto every advisor membership at that firm,
    // and `connect` does the same for a new connection because `people` is now
    // {@link SHARED_BY_DEFAULT}.
    //
    // The deploy ordering this sat behind is DISCHARGED, not repealed, and it still binds the
    // next additional id anyone adds: the role has to EXIST in an environment before anything
    // names it, because appcore's `assertAssignableRoles` refuses an unseeded roleId while
    // `updateMember`/`issueInvite` write the whole `roleIds` array — so composing an unseeded
    // id fails the ENTIRE membership write, meaning Connect and Save-shared-areas for every
    // family and every area, not just this one. The seed of `steward-share-people-tax-id`
    // shipped a release earlier (PR 2 of 4, #1450). Whether that seed has RUN in any given
    // environment is deploy state, is not claimed here, and is verified at source per
    // environment before promotion.
    //
    // ⚠️⚠️ **REVERTING PR 4 IS NOT A NO-OP AND IT DOES NOT SELF-HEAL. READ THIS FIRST IF YOU
    // ARE REVERTING AT 2AM.** A revert restores `areaRoleIds`' `.map` and `people: false`, but
    // it does NOT restore `PILLAR_ROLE_IDS` to a `map`: that became a `flatMap` in PR 2 of 4
    // (#1450) and is outside the revert. So after a revert:
    //
    //   - every membership already granted `steward-share-people-tax-id` KEEPS it. Nothing
    //     scans for it and nothing expires it;
    //   - it is removed only when THAT family next saves any area switch — `desired` no longer
    //     contains the id while `PILLAR_ROLE_SET` still strips it — which may be never;
    //   - it is invisible to every area-keyed census, because `areasOfRoleSet` keys on the
    //     PRIMARY id alone and reports the same `people` either way.
    //
    // The result is a standing, unaudited ability to decrypt a social security number, of
    // unbounded duration, on a codebase that says the feature was reverted. **So a revert must
    // be paired with a sweep of advisor memberships' `roleIds` for
    // `01HZSTEWRDPTAXID00000001`**, and the sweep is the part that actually withdraws the
    // grant; the code revert only stops new ones. Do not treat the revert as complete before
    // the sweep has run in every environment the revert reaches.
    additionalRoleIds: [PEOPLE_TAX_ID_REVEAL_ROLE_ID],
    offRoleIds: [],
    // ARMED 2026-09-14 (steward-app#1260 phase 2): `person` declares
    // `readCapability: 'people:read'` in `STEWARD_APP_TYPE_DEFS`. It reached this table one
    // release EARLIER with no declaration, which is the two-deploy ordering the module
    // docblock argues for and not an oversight to repeat in one PR.
    //
    // The household roster and the per-person record (steward-app#1260): legal name, date
    // of birth, relationship, phone, email (#1957), addresses, trusted contact. Every one of those is identity
    // data about a named individual, which is why it is its own disclosure domain rather
    // than a corner of Contacts — an RIA needs it at account opening and can be cut off
    // afterwards, a CPA needs it every year, and a family that shares its address book
    // has not thereby agreed to share its children's dates of birth.
    //
    // ⚠️ **AND the tax identification number, on both of its tiers** (steward-app#1452,
    // founder ruling 2026-09-15). Two disclosures, not one, and they are not equivalent:
    //
    //   - **The last four digits ride `people:read`, unaudited.** `person.taxIdHint` is
    //     declared `derived` rather than `sensitive` on purpose (a hint nobody can fetch
    //     masks nothing), so appcore does not strip it on read and the façade deliberately
    //     leaves it out of `router.ts`'s envelope strip list. Any principal holding this
    //     pillar's `capability` in the family's workspace therefore receives it on every
    //     person row, with no audit entry and no separate gesture. #1452 offered stripping
    //     it from advisor reads or gating it behind the reveal capability; both were
    //     DECLINED, so this is the designed behaviour and not a gap to close.
    //   - **The full number needs `people:reveal-tax-id`**, which is
    //     {@link SharingPillar.additionalRoleIds}'s role, and is rate-limited and written to
    //     the audit log before any plaintext leaves the server.
    //
    // This paragraph is the record of WHAT the area discloses, and it is load-bearing
    // because the family-facing copy has to agree with it: Settings, What you share
    // (`src/pages/people/WhatYouShare.tsx`, the Family switch's note) carries it in plain
    // words, pinned by `WhatYouShare.test.tsx`. As of 2026-10-01 the connect confirmation
    // (`src/pages/people/AdvisorReview.tsx`) no longer does: Family is not in the default a
    // new connection starts with, so that dialog says the advisor will NOT see family details,
    // and the Social Security wording is read where Family is turned on. (An ADVISOR-EXISTING
    // firm composes that firm's current areas instead, and the dialog then says so without
    // listing them.) Historically the copy landed in the SAME commit as the grant, deliberately:
    // no family may be granted more than its description says.
    typeCodes: ['person'],
  },
];

/**
 * Pillars that are GRANTED but NOT YET ARMED, for the length of the two-deploy gap.
 *
 * This is the scoped return of phase 1's `NOTHING IS ARMED` guard, which phase 2b deleted
 * because by then it covered nothing. The grant → arm ordering that block protected is not
 * a one-time phase of #739: the module docblock above states it as the rule for every
 * pillar added since, and `assistantAccess.ts` runs the identical sequence one axis over.
 * The problem phase 2b left behind is that the `EVERY PILLAR IS ARMED` block is TOTAL over
 * {@link SHARING_PILLARS} — TWO of its assertions red on any pillar whose types carry no
 * declaration, and `typeCodes: []` does not dodge them either, because the second compares
 * the set of declared strings against `[pillar.capability]`. (The block's exact-literal
 * `readCapabilities` census is NOT one of the two: an unarmed type contributes no entry, so
 * that literal is untouched by a grant PR — which is itself the signal that a grant PR whose
 * diff moves it has armed something. Verified by mutation, 2026-09-14.) So with no exemption
 * a phase-1 grant PR **cannot be expressed at all**, and the only way past the suite is the
 * collapse the whole ordering exists to forbid: grant and arm in one artifact, where a warm
 * Lambda serves a stale role (no capability) against an armed type and denies every family
 * member for up to a minute per container, while the seed reports complete success.
 *
 * **A pillar named here is not unguarded, it is guarded in the other direction.**
 * `pillars.test.ts` skips those two arm assertions for it and asserts the inverse instead:
 * no pack type may declare its capability, and its own types must declare no
 * `readCapability` at all. That is the actual phase-1 property, and it reds if somebody arms
 * early — which is the mistake this list exists to catch, not a mistake it excuses.
 *
 * **The steady state is an empty array.** A pillar belongs here for the minutes-to-days
 * between the grant deploy and the arm deploy, and removing it is half of the arm PR. A
 * `people` entry still sitting here a week after `person` was armed would be a stale
 * exemption silently disabling two real assertions for a live pillar — so the test file
 * pins this list as an exact literal (the way `SHARED_BY_DEFAULT` is pinned), asserts every
 * id here is a real {@link SHARING_PILLARS} id, and leans on the exact-literal
 * `readCapabilities` census as the backstop that notices the arm itself either way.
 *
 * **EMPTY as of 2026-09-14. That is the steady state, not evidence the mechanism is dead.**
 * Its one occupant so far was `people` (steward-app#1260): granted by phase 1, armed by
 * phase 2 a release later, and removed from here in the same commit that armed `person`.
 *
 * Keep this constant, and keep the argument above with it. The next pillar needs both, and
 * the failure it prevents — a warm Lambda serving a stale role against an armed type,
 * denying every family member for up to a minute per container while the seed reports
 * complete success — is invisible to every test that exists, because it is a property of
 * two caches in a different repo and not of this source tree.
 */
export const PILLAR_GRANTED_NOT_YET_ARMED: readonly SharingPillarId[] = [];

/**
 * The pillars a FAMILY-FACING DISCLOSURE SURFACE may offer — {@link SHARING_PILLARS} minus
 * whatever is mid-sequence. Derived, never hand-written, so the two cannot disagree.
 *
 * ## Why an unarmed pillar must not reach the consent screen
 *
 * People → Advisors → What you share says, in as many words, *"Turning an area off means your
 * advisors at «firm» cannot see it."* For a pillar whose types declare no
 * `readCapability`, that sentence is **false**: nothing is withheld, because nothing is
 * gated. `pillars.test.ts` calls this "the false claim the whole phase-1/phase-2 split
 * existed to keep off the screen", and it would arrive by a route phase 2b never had to
 * think about, because in #739 the pillar table and the control shipped together.
 *
 * The same argument covers the wire, not just the pixels: the façade's `parseAreas` turns
 * caller text into pillar ids and `areaRoleIds` composes the matching role onto an advisor
 * membership. Accepting `people` there would let a client record a sharing decision that
 * discloses and withholds nothing, and would then read it back as though it meant
 * something.
 *
 * ## ⚠️ The asymmetry, which is the whole risk of a grant-only release
 *
 * **Exactly three call sites take the LIVE set** (as of 2026-09-14, and
 * `pillars.test.ts` pins the split in both directions rather than leaving it to this
 * sentence): `src/pages/people/WhatYouShare.tsx`'s render loop, and the façade's
 * `parseAreas` and `areaRoleIds` in `services/steward-app-api/src/handlers/advisor.ts`.
 * Those are the surfaces that OFFER a choice or ACT on one.
 *
 * **Everything else keeps using the TOTAL table, and the reasons differ per kind:**
 *
 * - {@link PILLAR_CAPABILITIES}, {@link PILLAR_ROLE_IDS} and the `packRoles.ts` grants —
 *   the grant IS phase 1. Narrowing these would mean the grant never ships, and the arm
 *   deploy would then find the family roles without the string: the exact lockout the
 *   ordering exists to prevent.
 * - **The advisor-scope STRIP and read-only lists** — `ADVISOR_SCOPE_ROLE_IDS` and
 *   `NO_WRITE_ROLE_IDS` in `roles.ts`, `isAdvisorScopeRoleId`, and `PILLAR_ROLE_SET` in the
 *   façade. ⚠️ **A safety list that goes partial is steward-app#732/#787's cross-firm
 *   leak.** These answer "is this an id every advisor of every firm can hold, and therefore
 *   one that must never be written onto a folder ACL as a FAMILY principal?" The answer for
 *   an in-flight pillar role is YES — it is a pack-global id like any other — and filtering
 *   it out would stop it being stripped. Unarmed is not the same as harmless.
 * - `areasOfRoleSet` in the façade — it REPORTS what a membership actually carries. Hiding
 *   a role that is genuinely present would be the false claim in the other direction.
 *
 * In the steady state this is `SHARING_PILLARS` exactly, and the distinction costs nothing.
 * **That is the state on 2026-09-14**, with {@link PILLAR_GRANTED_NOT_YET_ARMED} empty: this
 * constant and `SHARING_PILLARS` currently hold the same five entries, and every consumer
 * that turns on the difference is exercising the empty case. The split is kept for the next
 * pillar, not because one is in flight — and `pillars.test.ts` pins it in both directions so
 * that it still works the day one is.
 */
export const LIVE_SHARING_PILLARS: readonly SharingPillar[] =
  SHARING_PILLARS.filter(p => !PILLAR_GRANTED_NOT_YET_ARMED.includes(p.id));

/**
 * Look up a pillar a disclosure surface may act on, by its stable id.
 *
 * {@link pillarById} deliberately stays TOTAL — it is the table's own lookup and is used to
 * resolve ids that already exist on a membership. This is the narrowed one: it returns
 * `undefined` for a pillar that is granted but not yet armed, so caller text naming such an
 * area is refused as unknown rather than honoured into a sharing decision that means
 * nothing. Callers on the request path must treat `undefined` as a 400, never as
 * "no restriction".
 */
export function livePillarById(id: string): SharingPillar | undefined {
  return LIVE_SHARING_PILLARS.find(p => p.id === id);
}

/**
 * Pack types deliberately outside every pillar — see the module doc comment for the
 * reasoning on each. Exhaustiveness over `STEWARD_APP_TYPE_DEFS` is asserted in
 * `pillars.test.ts`, so adding a type to the pack without classifying it here reds the
 * suite instead of silently defaulting to "an advisor sees it".
 *
 * ⚠️ **"Ungated" here means ungated BY A PILLAR, never ungated outright.** Three of the
 * codes below (`credential`, `credential-vault`, `chat-session`) are gated harder than any
 * pillar: they carry a fixed-rule `readCapability` a client cannot switch back on. Read
 * the per-entry comments rather than the constant's name.
 */
export const PILLAR_UNGATED_TYPE_CODES: readonly string[] = [
  // Withheld from advisors by a FIXED rule, not a client choice (#643).
  'credential', 'credential-vault',
  // ⚠️ Named here because no PILLAR governs it, NOT because it is ungated: it is
  // withheld from advisors by a FIXED rule, `assistant:read`, armed on the type since
  // #932 phase 2 (`assistantAccess.ts`). Never a pillar, in either phase.
  'chat-session',
  // The family's own app-internal records.
  'feedback',
  // The advisor channel's own machinery.
  'advisor-connection', 'advisor-intake', 'onboarding-request', 'onboarding-confirmation',
  // Sponsored client setup without a submitted intake (steward-app#1543 part (b)) — the
  // no-intake sibling of `advisor-intake`, same reasoning: it is the invitation record
  // itself, not client data a pillar could ever govern.
  'sponsored-invitation',
  // The magic-link intake channel's own invitation record (steward-app#1555), same
  // reasoning as `sponsored-invitation` immediately above and the same shape: an advisor's
  // typed address and a draft id, not client financial data a pillar could ever govern.
  'intake-invitation',
  // A firm hiding one of its own unclaimed prospect drafts from its Prospects list
  // (steward-app#1570) — the advisor channel's own bookkeeping about the FIRM's list, not
  // client financial data. A client has no membership in the firm workspace this type
  // lives in and no pillar toggle could ever reach it either way.
  'draft-dismissal',
  // ⚠️ Cash flow (steward-app#2581, docs/39 §8.2). Named here because no PILLAR governs them,
  // NOT because they are ungated. Each is armed with a FIXED-rule `readCapability`, as
  // `chat-session` is (`cash-flow:read`, `cash-flow:transactions`, `cash-flow-cursor:read`;
  // slice 4, steward-app#2583; `cashFlowTypeDefs.ts`). They cannot be a pillar because a pillar's capability is spread into
  // every base family role, and cash flow is per viewer: no base role holds either string.
  // A firm reaches them only through the two standalone cash flow firm roles; how those
  // appear in shared areas is slice 10's (steward-app#2589).
  'transaction', 'transfer-pair', 'cash-flow-month', 'cash-flow-agreement', 'transaction-cursor',
];

/** Every pillar capability, in table order. */
export const PILLAR_CAPABILITIES: readonly string[] = SHARING_PILLARS.map(p => p.capability);

/**
 * Every pillar role id, in table order.
 *
 * ⚠️ **These are ADVISOR-scope roles. They are never family roles**, and anything that
 * derives a "family" principal set by harvesting the roles active members actually hold
 * must strip them, exactly as it strips `STEWARD_ROLE_IDS.ADVISOR` and for the same
 * reason. `familyRoleIds()` in `services/steward-app-api/src/handlers/advisor.ts` is that
 * place today, and its comment rests on an invariant phase 2 breaks: *"an advisor from
 * any firm holds this role and, through this façade, nothing else"*. Once a membership is
 * `[ADVISOR, …pillar roles]`, a pillar role harvested into one firm's shared-folder ACL
 * is readable by every OTHER firm sharing the same area — steward-app#732's cross-firm
 * leak, arriving by a new route. Exported here so that strip is a set difference and not
 * a fourth hand-maintained list. Detail: steward-app#739.
 *
 * ⚠️ **`flatMap`, not `map`, since steward-app#1260 (2026-09-15): EVERY role id of every
 * pillar, primary and {@link SharingPillar.additionalRoleIds} alike.** That one word is what
 * carries a pillar's second role into both strip lists (`roles.ts` spreads this constant
 * twice) and into the façade's `PILLAR_ROLE_SET`, with no edit at either.
 *
 * ⚠️⚠️ **This constant does NOT carry an additional id through the `setSharedAreas` WRITE on
 * its own; `areaRoleIds` in the façade has to use the identical `flatMap`, and until
 * steward-app#1260 PR 4 (2026-09-15) it did not.** That write is
 * `[...current.filter(r => !PILLAR_ROLE_SET.has(r)), ...PILLAR_ROLE_IDS.filter(r => next.has(r))]`,
 * where `next` derives from `areaRoleIds`. `PILLAR_ROLE_SET` is this constant, so the `flatMap`
 * above is exactly what puts an additional id into the set the write STRIPS FIRST — and while
 * `areaRoleIds` still emitted `roleId` alone, nothing put it back. The next save of ANY area
 * switch would then silently revoke the additional role on every advisor membership at that
 * firm. It fails CLOSED, so it is a correctness and UX defect rather than a leak, and that is
 * what makes it hard to find: the family believes they are sharing, the advisor sees a 403
 * they cannot explain, nothing in the UI shows it, and the cause is a save made for an
 * unrelated area. Found by review on this branch on 2026-09-15, before anything composed an
 * additional role, and closed by PR 4 in the same commit that first composed one.
 *
 * **The rule that outlives the fix: these two derivations move TOGETHER, and neither is safe
 * alone.** `services/steward-app-api/src/__tests__/advisorSharedAreas.test.ts` pins both
 * directions — a save that still names the Family area keeps the reveal role on every advisor
 * membership, and turning Family off removes BOTH `people` ids — and reverting either
 * derivation to `map` reds it (verified by mutation, 2026-09-15; the echoed lines are in that
 * PR's description).
 *
 * A `map` over `roleId` alone HERE would instead leave an additional role UNSTRIPPED while
 * every consumer still looked total, which is steward-app#732/#787's cross-firm leak: a
 * pack-global id harvested onto one firm's shared-folder ACL as a FAMILY principal is
 * readable by every other firm sharing that area. `pillars.test.ts` asserts the strip lists
 * over every id here, not over `SHARING_PILLARS.map(p => p.roleId)`, so the two cannot drift
 * (verified by mutation: dropping the reveal role from `ADVISOR_SCOPE_ROLE_IDS` reds
 * `🔒 the advisor-scope STRIP lists stay TOTAL`).
 *
 * So this is NOT one id per pillar and nothing may assume it is. Its length is
 * `SHARING_PILLARS.length` plus the additional ids, and a count derived from the number of
 * pillars is a count that will be wrong.
 */
export const PILLAR_ROLE_IDS: readonly string[] =
  SHARING_PILLARS.flatMap(p => [p.roleId, ...p.additionalRoleIds, ...p.offRoleIds]);

/** Every role that READ-CONFINES its holder (steward-app#1950): the pillars' `offRoleIds`.
 *  The set the façade drops from a membership that also holds a family role. */
export const READ_CONFINING_ROLE_IDS: readonly string[] =
  SHARING_PILLARS.flatMap(p => p.offRoleIds);

/** Look up a pillar by its stable id. Returns `undefined` for an unknown area — callers
 *  on the request path must treat that as a 400, never as "no restriction". */
export function pillarById(id: string): SharingPillar | undefined {
  return SHARING_PILLARS.find(p => p.id === id);
}

/**
 * Which areas a BRAND NEW connection starts out sharing (steward-app#739 phase 2a).
 *
 * ⚠️ **A per-pillar map, deliberately, and not a boolean "all on" flag.** The founder's
 * ruling of 2026-09-30 is portfolio and entities on, and documents, contacts and family
 * OFF, which no single flag can express — and the shape matters beyond that one ruling, because the
 * question "is this area shared by default?" is a product decision per area and has to be
 * answerable per area.
 *
 * **The type is the exhaustiveness check.** `Record<SharingPillarId, boolean>` is total
 * over the union, so adding a fifth pillar id makes THIS OBJECT a compile error until
 * somebody writes the answer down. That is the same argument `PILLAR_UNGATED_TYPE_CODES`
 * makes for pack types one paragraph up: a new area must force a deliberate answer, never
 * default to "shared" because nobody looked. `pillars.test.ts` pins the values as an exact
 * literal so revising the default is a visible diff, and pins the map's coverage at
 * runtime too, for the case where the union is ever widened to `string`.
 *
 * Revising a default never re-derives an ACCEPTED connection: its areas live on its advisor
 * memberships' `roleIds`, and `connect` reads them from there (`areasFromMembers`) whenever
 * the firm already has an advisor. It does reach an invitation that has not been accepted yet:
 * a still-pending invite whose roles differ from the pinned set is superseded, and re-issued
 * with the CURRENT default, when the family resends or re-connects
 * (`advisor.test.ts`, "re-issues a still-PENDING invite carrying the old default", and the
 * accepted-connection counterpart beside it).
 *
 * ⚠️ **That sentence is about THIS TABLE and does not generalise to "this pillar's grant".**
 * `people` composes a second role through {@link SharingPillar.additionalRoleIds}, and THAT
 * is re-derived on two live paths that reach existing connections — see the ⚠️⚠️ on `people`
 * below before reasoning about the blast radius of anything in this file.
 */
export const SHARED_BY_DEFAULT: Readonly<Record<SharingPillarId, boolean>> = {
  // Founder ruling, 2026-09-30 (#1969): a NEW connection starts with Portfolio and Entities
  // shared and Documents, Contacts and Family withheld. It supersedes the 2026-08-27 default
  // (contacts off, the rest on) and the 2026-09-15 ruling that put Family on. An ACCEPTED
  // connection is never re-derived from this table. A still-PENDING invite issued under the old
  // default is re-issued with this one when the family resends or re-connects.
  portfolio: true,
  // OFF by default. "Off" here still composes `DOCUMENTS_SHARED_FOLDER_ROLE_ID` through the
  // pillar's `offRoleIds` (#1950), so the family's shared folder, and the contribute opt-in
  // that depends on it, keep working while the full Documents area stays withheld.
  documents: false,
  // OFF by default (founder ruling, 2026-08-27, kept 2026-09-30). A family's address book is
  // the pillar a firm is least likely to need and the one a family is most surprised to have
  // shared, so it starts withheld and the family can turn it on in one gesture.
  contacts: false,
  entities: true,
  // OFF by default (founder ruling, 2026-09-30). This REVERSES the 2026-09-15 ruling, which
  // had the area ON because opening accounts at a custodian needs the household's legal
  // names, dates of birth and addresses. The family can turn Family on when a firm needs it.
  //
  // The history below is kept because it is still the record of what this switch composes:
  // the Family switch grants BOTH the read role and the tax-identification reveal role
  // (`additionalRoleIds` above), so turning it on lets a connection reveal a social security
  // number without a second gesture. That is deliberate (founder, 2026-09-15: one switch
  // now, two capabilities underneath, a second switch later if it is wanted). The reveal is
  // capability-gated, rate-limited, and audited before any plaintext leaves the server.
  //
  // ⚠️⚠️ **DO NOT read the usual "an accepted connection is never re-derived" line into the
  // 2026-09-15 change that turned Family on.**
  // That line (the docblock above) is true of this TABLE: nothing re-reads
  // `SHARED_BY_DEFAULT` for an accepted connection. It is FALSE of that change,
  // because the reveal role arrives through `additionalRoleIds` and
  // `PILLAR_ROLE_IDS`, and BOTH of those ARE re-derived, on two live paths. Traced at source
  // in `services/steward-app-api/src/handlers/advisor.ts` on 2026-09-16, by this PR's
  // adversarial security pass and again by the author:
  //
  //   1. **Any save of any switch RETRO-GRANTS the reveal.** `setSharedAreas` computes
  //      `desired = areaRoleIds(areas)` and writes
  //      `[...current.filter(r => !PILLAR_ROLE_SET.has(r)), ...PILLAR_ROLE_IDS.filter(r => next.has(r))]`.
  //      Both of those now carry the reveal id. So a family whose Family area was ALREADY on,
  //      who ticks Contacts and presses save, adds `steward-share-people-tax-id` to every
  //      registry advisor at that firm — out of a gesture about Contacts, with no dialog and
  //      no disclosure in front of it. (The SPA echoes the GET's reported areas back on every
  //      write, so `people` is in that request whether or not the family touched it.) A
  //      stored-only address is spared, because rule C clamps it to pillar roles it already
  //      held. Pinned by `services/steward-app-api/src/__tests__/advisorSharedAreas.test.ts`,
  //      "🔒 a pre-PR-4 membership GAINS the tax-identification role on the next save of an
  //      unrelated area".
  //   2. **Inviting a SECOND advisor gives them more than the first one has.** `connect`
  //      derives the areas from the firm's existing memberships (`areasFromMembers` →
  //      `areasOfRoleSet`, which keys on PRIMARY ids alone) and re-emits them through
  //      `areaRoleIds`, so the new invitee's `roleIds` carry BOTH `people` ids. The pin loop
  //      then skips every member already holding `STEWARD_ROLE_IDS.ADVISOR`, so advisor A is
  //      left with the read role alone. One family choice, made once, ends with advisor B able
  //      to decrypt a social security number and advisor A not.
  //
  // Path 2 also puts a crack in `connect`'s own stated invariant that areas union PER FIRM
  // (the ⚠️ on `registryTargets`): the union still holds at AREA granularity and no longer at
  // ROLE granularity, which is the granularity that decides who can reveal. And the asymmetry
  // is invisible from every reporting surface, because `areasOfRoleSet` answers `people` for
  // both memberships — `GET /advisor/shared-areas` and the family's screen cannot show it.
  //
  // Neither path is a defect this file can fix, and neither is a reason to revert on its own;
  // they are what "one switch, two capabilities underneath" costs. They ARE the reason a
  // revert needs a `roleIds` sweep rather than a redeploy: see `additionalRoleIds` above.
  people: false,
};

/**
 * The default area ids, in table order — derived from {@link SHARED_BY_DEFAULT} rather
 * than written out a second time, so the two cannot disagree.
 *
 * This is what `connect` composes onto a new advisor invitation's `roleIds`. It is NOT
 * consulted anywhere else: once a connection exists, its memberships are the only truth
 * about what it shares.
 */
export const DEFAULT_SHARED_AREA_IDS: readonly SharingPillarId[] =
  SHARING_PILLARS.filter(p => SHARED_BY_DEFAULT[p.id]).map(p => p.id);
